import React, { useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { useSearchParams } from "react-router-dom";
import { useResultData } from "../hooks/useResultData";
import { toDisplayString } from "../components/string";
import type { CatKey } from "../types";
import { useLocalization } from "../hooks/useLocalization";

/* ================= styled ================= */

const Page = styled.div`
  position: relative;
  width: 100%;
  /* 讓內容自然決定高度，頁面就會可滾動（由外層 Shell 決定整體高度） */
  // min-height: 100svh;
  margin: 0 auto;
  // padding: 12px 0px calc(env(safe-area-inset-bottom, 0px) + 20px);
  display: flex;
  flex-direction: column;
  color: #e9eefc;
  z-index: 10;
  align-items: center;
`;

const Hero = styled.section`
  position: relative;
  margin-bottom: 100px;
  min-height: clamp(220px, 42vw, 360px); /* 可依需求微調 */
  width: 100%;
  padding: 0 16px;

  /* 針對平板尺寸 */
  @media (min-width: 768px) and (max-width: 1024px) {
    min-height: clamp(390px, 53vw, 540px);
  }
`;

const ContentSection = styled.section`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 0 100px;
  width: 88%;
`;

const Watermark = styled.div<{ $text: string }>`
  pointer-events: none;
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10; /* 低於盾徽，避免蓋住 */
  display: flex;
  align-items: center; /* 垂直置中（對自身行高） */
  justify-content: center; /* 水平置中 */

  /* 字體樣式（依你給的 Figma） */
  font-family: "PingFang TC", sans-serif;
  font-style: normal;
  font-weight: 600;
  line-height: 84px;
  text-align: center;
  letter-spacing: 1px;

  /* 依文字長度自動調整字級，避免過長溢出 */
  font-size: ${({ $text }) => {
    const len = $text?.length ?? 0;
    if (len <= 5) return "4.5rem";
    if (len <= 8) return "4rem";
    if (len <= 12) return "3.5rem";
    if (len <= 18) return "3rem";
    return "2.5rem";
  }};

  /* 漸層透明字（你提供的樣式） */
  background: linear-gradient(
    92.81deg,
    rgba(255, 255, 255, 0) -3.72%,
    rgba(255, 255, 255, 0.8) 99.11%
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  opacity: 0.15; /* 保留水印淡度 */
  &:after {
    content: attr(data-text);
  }

  /* 針對平板尺寸 */
  @media (min-width: 768px) and (max-width: 1024px) {
    line-height: 1;
    font-size: ${({ $text }) => {
      const len = $text?.length ?? 0;
      if (len <= 5) return "8rem";
      if (len <= 8) return "7.5rem";
      if (len <= 12) return "7rem";
      if (len <= 18) return "6.5rem";
      return "6rem";
    }};
  }
`;

const ShieldWrap = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  margin-top: 6px;
  z-index: 11;
`;

const ShieldImg = styled.img`
  width: 40%;
  height: auto;
  display: block;
  user-select: none;
`;

const Hand = styled.img`
  position: absolute;
  top: min(300px, 20vh);
  right: 0%;
  width: 80vw;
  height: auto;
  object-fit: contain;
  pointer-events: none;
  user-select: none;
  z-index: 2;
`;

/* ===== Reminder（依 Figma） ===== */

/* 外層卡：透明底 + 27px 圓角 + 置中 */
const NoticeCard = styled.div`
  border-radius: 27px;
  background: rgba(0, 0, 0, 0.001); /* 幾乎透明，保留容器 */
  text-align: center;
  z-index: 20;
  box-shadow:
    0 6px 18px rgba(0, 0, 0, 0.28),
    inset 0 0 0 1px rgba(255, 255, 255, 0.04);
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
`;

/* 標題列：icon + 漸層字 */
const NoticeHeader = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  height: 34px; /* 對齊 Figma title 高度 */
`;

/* 21x21 的小 icon（可用你現有 sparkles 或任意 svg/img） */
const NoticeIcon = styled.img`
  width: 21px;
  height: 21px;
  display: block;

  /* 針對平板尺寸 */
  @media (min-width: 768px) and (max-width: 1024px) {
    width: 31px;
    height: 31px;
  }
`;

/* 「提醒您」：20px、字距 1px、漸層白字＋陰影（依 Figma） */
const NoticeTitleText = styled.div`
  font-family: "PingFang TC", system-ui, sans-serif;
  font-weight: 500;
  font-size: 20px;
  line-height: 34px;
  letter-spacing: 1px;
  background:
    linear-gradient(0deg, #ffffff, #ffffff),
    linear-gradient(90deg, #757ea7 -8.1%, #ffffff 53.63%, #3e466d 108.63%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-shadow: 0px 4px 7.6px rgba(0, 0, 0, 0.25);

  /* 針對平板尺寸 */
  @media (min-width: 768px) and (max-width: 1024px) {
    font-size: 32px;
  }
`;

/* 內文：16px / 22px、白、字距 1px、柔和陰影（依 Figma） */
const NoticeText = styled.div`
  margin: 8px auto 14px;
  /* max-width: 316px; Figma 寬度 */
  color: #ffffff;
  font-family: "PingFang TC", system-ui, sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 22px; /* 138% */
  letter-spacing: 1px;
  text-shadow: 0px 4px 14.1px rgba(13, 15, 58, 0.52);
  text-align: left;

  /* 針對平板尺寸 */
  @media (min-width: 768px) and (max-width: 1024px) {
    font-size: 20px;
    line-height: 1.5;
  }
`;

/* CTA 按鈕：163x38、漸層藍、pill、外發光與下陰影（依 Figma） */
const PrimaryBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 163px;
  min-height: 38px;
  margin: 0 auto;
  border: 0;
  border-radius: 110px;
  background: linear-gradient(360deg, #0c34f8 0%, #90b3ff 100%);
  box-shadow:
    0px 0px 32.8px #134eff,
    0px 4px 6.1px rgba(0, 0, 0, 0.25);
  color: #ffffff;
  font-family: "PingFang TC", system-ui, sans-serif;
  font-weight: 500;
  font-size: 18px;
  line-height: 14px; /* 視覺與 Figma 對齊 */
  letter-spacing: 1px;
  cursor: pointer;
  transition:
    filter 0.15s ease,
    transform 0.06s ease;
  &:hover {
    filter: brightness(1.05);
  }
  &:active {
    transform: translateY(1px);
  }

  /* 針對平板尺寸 */
  @media (min-width: 768px) and (max-width: 1024px) {
    font-size: 24px;
    line-height: 24px;
    padding: 18px 40px;
  }
`;

const SectionTitle = styled.h3`
  margin: 0;
  text-align: center;

  /* 字型設定 */
  font-family: "PingFang TC", system-ui, sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 36px;
  line-height: 1;
  letter-spacing: 3px;

  /* Figma 的漸層白字 */
  background: linear-gradient(
    90deg,
    #757ea7 -8.1%,
    #ffffff 53.63%,
    #3e466d 108.63%
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-fill-color: transparent;

  /* 可選：微陰影讓字更立體 */
  text-shadow: 0px 4px 7.6px rgba(0, 0, 0, 0.25);

  /* 針對平板尺寸 */
  @media (min-width: 768px) and (max-width: 1024px) {
    font-size: 50px;
  }
`;

const SectionCard = styled.section`
  border-radius: 14px;
  padding: 12px;
  background: rgba(15, 27, 60, 0.45);
  border: 1px solid rgba(255, 255, 255, 0.14);
  box-shadow:
    0 6px 18px rgba(0, 0, 0, 0.28),
    inset 0 0 0 1px rgba(255, 255, 255, 0.04);
`;

/* ========== 公開資訊（Carousel 區） ========== */

/* 整體外層 */
const CarouselWrap = styled.div`
  width: 100%;
  margin: 0 auto;
  margin-left: 24px;
  display: flex;
  gap: 12px;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  padding: 6px 4px 10px;
  -webkit-overflow-scrolling: touch;
  &::-webkit-scrollbar {
    display: none;
  }

  /* 針對平板尺寸 */
  @media (min-width: 768px) and (max-width: 1024px) {
    margin-left: 52px;
  }
`;

/* 單張卡片 */
const CarouselCard = styled.a`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex: 0 0 auto;
  width: 240px;
  height: 149px;
  border-radius: 27px;
  background: rgba(0, 0, 0, 0.001);
  scroll-snap-align: start;
  position: relative;
  padding: 23px 20px 16px;
  text-decoration: none;
  box-sizing: border-box;
  box-shadow:
    0 6px 18px rgba(0, 0, 0, 0.28),
    inset 0 0 0 1px rgba(255, 255, 255, 0.04);

  /* 針對平板尺寸 */
  @media (min-width: 768px) and (max-width: 1024px) {
    width: 420px;
    height: 260px;
    padding: 26px 35px;
  }
`;

/* 內文文字 */
const CarouselText = styled.div`
  font-family: "PingFang TC", system-ui, sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 22px;
  letter-spacing: 1px;
  color: #ffffff;
  text-shadow: 0px 4px 7.6px rgba(13, 15, 58, 0.52);
  /* max-width: 198px; */
  margin-bottom: 18px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 3; /* 最多三行 */
  -webkit-box-orient: vertical;

  /* 針對平板尺寸 */
  @media (min-width: 768px) and (max-width: 1024px) {
    font-size: 20px;
    line-height: 1.5;
  }
`;

/* 日期文字 */
const CarouselDate = styled.div`
  font-family: "PingFang TC", system-ui, sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 22px;
  letter-spacing: 1px;
  color: #ffffff;
  text-shadow: 0px 4px 7.6px rgba(13, 15, 58, 0.52);
  opacity: 0.9;

  /* 針對平板尺寸 */
  @media (min-width: 768px) and (max-width: 1024px) {
    font-size: 20px;
  }
`;

const BottomCta = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0px 1.5rem;
  min-height: 38px;
  margin: 50px auto;
  border: 0;
  border-radius: 110px;
  background: linear-gradient(360deg, #0c34f8 0%, #90b3ff 100%);
  box-shadow:
    0px 0px 32.8px #134eff,
    0px 4px 6.1px rgba(0, 0, 0, 0.25);
  color: #ffffff;
  font-family: "PingFang TC", system-ui, sans-serif;
  font-weight: 500;
  font-size: 18px;
  line-height: 14px; /* 視覺與 Figma 對齊 */
  letter-spacing: 1px;
  cursor: pointer;
  transition:
    filter 0.15s ease,
    transform 0.06s ease;
  &:hover {
    filter: brightness(1.05);
  }
  &:active {
    transform: translateY(1px);
  }

  /* 針對平板尺寸 */
  @media (min-width: 768px) and (max-width: 1024px) {
    font-size: 24px;
    line-height: 24px;
    padding: 18px 40px;
  }
`;

const DisclaimerText = styled.p`
  text-align: center;
  font-size: 0.5rem;
  color: rgba(255, 255, 255, 0.6);
  margin-top: 16px;
  padding: 0 16px;

  /* 針對平板尺寸 */
  @media (min-width: 768px) and (max-width: 1024px) {
    font-size: 0.875rem;
  }
`;

/* ---------- Modal ---------- */
const ModalBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 50;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
`;
const ModalCard = styled.div`
  position: relative;
  width: 100%;
  max-width: 460px;
  border-radius: 18px;
  background: #ffffff;
  color: #111827;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 30px 70px rgba(0, 0, 0, 0.35);
  padding: 18px 16px 20px;
`;
const ModalTitle = styled.h4`
  margin: 0 0 12px;
  font-size: 16px;
  font-weight: 800;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  &:before {
    content: "✦";
  }

  /* 針對平板尺寸 */
  @media (min-width: 768px) and (max-width: 1024px) {
    font-size: 32px;
    padding-right: 20px;
    padding-left: 20px;
  }
`;
const ModalContent = styled.div`
  font-size: 14px;
  line-height: 1.2;
  color: #111827;

  /* 針對平板尺寸 */
  @media (min-width: 768px) and (max-width: 1024px) {
    font-size: 20px;
  }
`;
const CloseBtn = styled.button`
  position: absolute;
  right: 10px;
  top: 10px;
  height: 30px;
  width: 30px;
  border-radius: 9999px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: #f3f4f6;
  color: #111827;

  /* 針對平板尺寸 */
  @media (min-width: 768px) and (max-width: 1024px) {
    font-size: 20px;
    right: 20px;
    top: 20px;
    height: 40px;
    width: 40px;
  }
`;
const ModalClosePrimary = styled.button`
  margin: 14px auto 0;
  display: block;
  border-radius: 9999px;
  padding: 10px 18px;
  background: #111827;
  color: #fff;
  border: 0;

  /* 針對平板尺寸 */
  @media (min-width: 768px) and (max-width: 1024px) {
    font-size: 18px;
    padding: 12px 40px;
    margin: 30px auto 0;
  }
`;

/* ========== 頁首狀態提示（非輪詢） ========== */
const pulse = keyframes`
  0% { opacity: .6; transform: translateY(0); }
  50% { opacity: 1; transform: translateY(-1px); }
  100% { opacity: .6; transform: translateY(0); }
`;
const PendingBanner = styled.div`
  position: sticky;
  top: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: -4px;
  padding: 8px 12px;
  border-radius: 12px;
  background: rgba(59, 130, 246, 0.15);
  border: 1px solid rgba(59, 130, 246, 0.35);
  color: #dbeafe;
  font-size: 13px;
  backdrop-filter: blur(4px);
  animation: ${pulse} 2.2s ease-in-out infinite;
`;
const Dot = styled.span`
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 9999px;
  background: #93c5fd;
  box-shadow: 0 0 0 3px rgba(147, 197, 253, 0.15);
`;

const RefreshBtn = styled.button`
  margin-left: auto;
  border-radius: 9999px;
  padding: 6px 10px;
  border: 1px solid rgba(219, 234, 254, 0.6);
  background: rgba(219, 234, 254, 0.1);
  color: #e5edff;
  font-size: 12px;
`;

/* ========== Status Display Section ========== */
const StatusContainer = styled.div`
  margin: 16px 0;
  padding: 12px;
  background-color: rgb(247, 248, 250);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StatusRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StatusLabel = styled.span`
  font-family: monospace;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 200px;

  /* 針對平板尺寸 */
  @media (min-width: 768px) and (max-width: 1024px) {
    max-width: 280px;
  }
`;

const StatusText = styled.span<{ $color: string }>`
  color: ${({ $color }) => $color};
  font-weight: 500;
  flex-shrink: 0;
`;

/* ================= component ================= */

const VALID_CATEGORIES: ReadonlyArray<
  "SCAM" | "POTENTIAL_SCAM" | "NO_RISK_FOR_NOW" | "SAFE"
> = ["SCAM", "POTENTIAL_SCAM", "NO_RISK_FOR_NOW", "SAFE"];

export default function ResultPage() {
  const { t, locale } = useLocalization();

  // 從網址取 id，交給新版 hook
  const [searchParams] = useSearchParams();
  const idParam = searchParams.get("id");
  const id = idParam ? Number(idParam) : undefined;
  // 新 hook：不輪詢，進頁抓一次；需要時用 refetch(true) 手動更新
  const { data, loading, error, isImageOnly, refetch } = useResultData({ id });
  // Modal
  const [open, setOpen] = React.useState(false);

  const [reportStatus, setReportStatus] = React.useState<
    "idle" | "loading" | "success" | "error"
  >("idle");
  const reportInFlightRef = React.useRef(false);

  const callApi = React.useCallback(async () => {
    if (reportInFlightRef.current) return;
    reportInFlightRef.current = true;
    setReportStatus("loading");
    const apiLocale = locale === "en" ? "en" : "zh";
    try {
      const res = await fetch(
        `https://api.anti-es.yunlingpt.com/api/v1/reports?locale=${apiLocale}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            content: "CityGPT 防詐智慧城市",
            url: "https://anti-es.yunlingpt.com/",
            reporter_name: "Anonymous",
          }),
        },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status} ${res.statusText}`);
      setReportStatus("success");
    } catch (error) {
      console.error(error);
      setReportStatus("error");
    } finally {
      reportInFlightRef.current = false;
    }
  }, [locale]);

  useEffect(() => {
    callApi();
  }, [callApi]);

  // 狀態（0=pending,1=in progress,2=failed,3=success）
  const status = data?.status ?? null;
  const isSuccess = status === 3;
  const isFailed = status === 2;
  const showResult = !isImageOnly && !!data && isSuccess;

  useEffect(() => {
    if (status === 0 || status === 1) {
      const intervalId = setInterval(() => {
        refetch(true);
      }, 3000); // 每 3 秒輪詢一次

      // 當 status 改變或 component unmount 時清除 interval
      return () => clearInterval(intervalId);
    }
  }, [status, refetch]);

  // 失敗或 imageOnly 顯示 NO_RESULT；成功顯示分類；其餘不顯示結果區塊
  const categoryKey: CatKey | "NO_RESULT" | null = isImageOnly
    ? "NO_RESULT"
    : isFailed
      ? "NO_RESULT"
      : showResult
        ? VALID_CATEGORIES.includes(data!.category ?? "NO_RISK_FOR_NOW")
          ? (data!.category ?? "NO_RISK_FOR_NOW")
          : "NO_RISK_FOR_NOW"
        : null;

  const { topTitle, notice, wm, shield, hand } = (() => {
    const map = {
      SAFE: {
        topTitle: t("no_risk_found"),
        notice: t("no_risk_notice"),
        wm: t("security"),
        shield: "/images/shield-security.png",
        hand: "/images/robot-hand.png",
      },
      NO_RISK_FOR_NOW: {
        topTitle: t("no_risk_found"),
        notice: t("no_risk_notice"),
        wm: t("security"),
        shield: "/images/shield-security.png",
        hand: "/images/robot-hand.png",
      },
      POTENTIAL_SCAM: {
        topTitle: t("suspicious_content"),
        notice: t("suspicious_notice"),
        wm: t("suspicious"),
        shield: "/images/shield-suspicious.png",
        hand: "/images/robot-hand-yellow.png",
      },
      SCAM: {
        topTitle: t("scam_content"),
        notice: t("scam_notice"),
        wm: t("scam"),
        shield: "/images/shield-scam.png",
        hand: "/images/robot-hand-red.png",
      },
      NO_RESULT: {
        topTitle: t("unidentifiable_reupload"),
        notice: t("analysis_failed_notice"),
        wm: t("scam"),
        shield: "/images/shield-unknown.png",
        hand: "/images/robot-hand.png",
      },
    } as const;
    return categoryKey
      ? map[categoryKey]
      : { topTitle: "", notice: "", wm: "", shield: "", hand: "" };
  })();

  const newsList = showResult && Array.isArray(data?.news) ? data!.news : [];
  const contentList =
    showResult && Array.isArray(data?.content) ? data!.content : [];
  // 橫幅文案（0/1）— 只顯示，不輪詢
  const bannerText =
    status === 0
      ? t("request_sent_to_ai")
      : status === 1
        ? t("ai_analyzing")
        : null;

  // 在 component 內加這個 helper（放在 hooks 下面即可）
  const navigateThenRefresh = (to: string) => {
    window.location.assign(to);
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    if (open) document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open]);

  // 初次載入（尚無資料）
  if (loading && !data && !isImageOnly) {
    return (
      <Page>
        <SectionCard style={{ textAlign: "center", fontWeight: 700 }}>
          {t("loading")}
        </SectionCard>
      </Page>
    );
  }

  if (error && !isImageOnly) {
    return (
      <Page>
        <SectionCard
          style={{ textAlign: "center", color: "#ffb4b4", fontWeight: 700 }}
        >
          {t("error_occurred")}
          {String(error)}
        </SectionCard>
      </Page>
    );
  }

  const numbers = data?.numbers || [];
  const urls = data?.urls || [];

  return (
    <Page>
      {/* 狀態橫幅（可手動重新整理） */}
      {bannerText && (
        <PendingBanner>
          <Dot /> {bannerText}
          <RefreshBtn onClick={() => refetch(true)}>
            {t("refresh") ?? "重新整理"}
          </RefreshBtn>
        </PendingBanner>
      )}

      {/* 盾徽與提醒卡：成功(3) 或 失敗(2)/imageOnly -> NO_RESULT 才渲染 */}
      {categoryKey && (
        <>
          {/* HERO */}
          <Hero>
            {!!shield && (
              <ShieldWrap>
                <ShieldImg
                  src={shield}
                  alt="shield"
                  onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
                {!!wm && <Watermark data-text={wm} $text={wm} />}
              </ShieldWrap>
            )}
            <SectionTitle>{topTitle}</SectionTitle>
            <Hand
              src={hand}
              alt="hand"
              onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                e.currentTarget.style.display = "none";
              }}
            />
          </Hero>

          {/* 提醒卡（依 Figma） */}
          <ContentSection>
            <NoticeCard>
              <NoticeHeader>
                <NoticeIcon src="/images/sparkles-logo.svg" alt="icon" />
                <NoticeTitleText>
                  {t("reminder") /* 顯示「提醒您」 */}
                </NoticeTitleText>
              </NoticeHeader>

              <NoticeText>
                {locale === "en" && categoryKey
                  ? t(
                      {
                        SCAM: "reminder_scam_content",
                        POTENTIAL_SCAM: "reminder_suspicious_content",
                        SAFE: "reminder_safe_content",
                        NO_RISK_FOR_NOW: "reminder_safe_content",
                        NO_RESULT: "reminder_unclear_content",
                      }[categoryKey],
                    )
                  : categoryKey === "NO_RESULT"
                    ? notice
                    : data?.kindly_reminder || notice}
              </NoticeText>

              {/* 成功才開放「AI 如何判斷」按鈕 */}
              {showResult && (
                <PrimaryBtn type="button" onClick={() => setOpen(true)}>
                  {t("how_ai_judges")}
                </PrimaryBtn>
              )}
            </NoticeCard>
          </ContentSection>
        </>
      )}

      {/* 公開資訊（成功才顯示） */}
      {showResult && newsList.length > 0 && (
        <ContentSection style={{ width: "100%" }}>
          <SectionTitle>{t("public_information")}</SectionTitle>
          <CarouselWrap>
            {newsList.map((n, i) => (
              <CarouselCard
                key={i}
                href={n.url}
                target="_blank"
                rel="noreferrer"
              >
                <CarouselText>{n.title || "－"}</CarouselText>
                <CarouselDate>
                  {n.date
                    ? new Date(n.date).toLocaleDateString("zh-TW")
                    : new Date().toLocaleDateString("zh-TW")}
                </CarouselDate>
              </CarouselCard>
            ))}
          </CarouselWrap>
        </ContentSection>
      )}

      {/* 底部 CTA */}
      <BottomCta type="button" onClick={() => navigateThenRefresh("/chatbot")}>
        {t("check_other_content")}
      </BottomCta>

      <DisclaimerText>{t("result_disclaimer")}</DisclaimerText>

      {reportStatus === "error" && (
        <BottomCta type="button" onClick={callApi}>
          {t("retry") ?? "重新送出"}
        </BottomCta>
      )}

      {/* ===== Modal：AI 如何判斷（成功時） ===== */}
      {showResult && open && (
        <ModalBackdrop onClick={() => setOpen(false)}>
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <CloseBtn onClick={() => setOpen(false)}>✕</CloseBtn>
            <ModalTitle>{t("how_ai_judges_modal_title")}</ModalTitle>

            <ModalContent>
              {locale === "en" ? (
                <div style={{ marginTop: 8 }}>
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    <li style={{ marginBottom: 6 }}>
                      <strong>Numbers and URLs:</strong> Contains suspicious
                      phone numbers or website links.
                    </li>
                    <li style={{ marginBottom: 6 }}>
                      <strong>User reports:</strong> No related reports found at
                      the moment.
                    </li>
                    <li style={{ marginBottom: 6 }}>
                      <strong>Public information:</strong> Found discussions of
                      similar fraud cases.
                    </li>
                  </ul>
                </div>
              ) : (
                contentList.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    {contentList.map((c, i) => (
                      <p key={i} style={{ marginBottom: 6, paddingLeft: 30 }}>
                        {toDisplayString(c)}
                      </p>
                    ))}
                  </div>
                )
              )}
              {(numbers.length !== 0 || urls.length !== 0) && (
                <StatusContainer>
                  {numbers.length > 0 &&
                    numbers.map((n, i) => {
                      const getStatus = () => {
                        switch (n.spam_category) {
                          case "TELEMARKETING":
                            return {
                              text: t("status_telemarketing"),
                              color: "#6b7280",
                            };
                          case "CALLCENTER":
                            return {
                              text: t("status_callcenter"),
                              color: "#6b7280",
                            };
                          case "FRAUD":
                            return {
                              text: t("status_fraud"),
                              color: "#ef4444",
                            };
                          case "HARASSMENT":
                            return {
                              text: t("status_harassment"),
                              color: "#f97316",
                            };
                          case "HFB":
                            return {
                              text: t("status_hfb"),
                              color: "#3b82f6",
                            };
                          default:
                            return {
                              text: t("status_not_reported"),
                              color: "#6b7280",
                            };
                        }
                      };
                      const status = getStatus();
                      return (
                        <StatusRow key={`num-${i}`}>
                          <StatusLabel>{n.number}</StatusLabel>
                          <StatusText $color={status.color}>
                            {status.text}
                          </StatusText>
                        </StatusRow>
                      );
                    })}

                  {urls.length > 0 &&
                    urls.map((u, i) => {
                      const getStatus = () => {
                        switch (u.level) {
                          case "MALICIOUS":
                            return {
                              text: t("status_malicious"),
                              color: "#ef4444",
                            };
                          case "SUSPICIOUS":
                            return {
                              text: t("status_suspicious"),
                              color: "#f97316",
                            };
                          case "SAFE":
                            return {
                              text: t("status_safe"),
                              color: "#22c55e",
                            };
                          default:
                            return {
                              text: t("status_undefined"),
                              color: "#6b7280",
                            };
                        }
                      };
                      const status = getStatus();
                      return (
                        <StatusRow key={`url-${i}`}>
                          <StatusLabel>{u.url}</StatusLabel>
                          <StatusText $color={status.color}>
                            {status.text}
                          </StatusText>
                        </StatusRow>
                      );
                    })}
                </StatusContainer>
              )}
            </ModalContent>

            <ModalClosePrimary onClick={() => setOpen(false)}>
              {t("close")}
            </ModalClosePrimary>
          </ModalCard>
        </ModalBackdrop>
      )}
    </Page>
  );
}
